package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/** Resolved wind-down state: whether the banner/charge-block is active and the localized message. */
data class SunsetState(
    val enabled: Boolean = false,
    val message: LocalizedText = LocalizedText(),
)

/**
 * Graceful-sunset state, pushed from Remote Config at the composition root (`sunset_mode_enabled` +
 * `sunset_message_{en,fr,fa}`). When [SunsetState.enabled] is true the app shows an app-wide banner and
 * — from Phase 6 — every purchase/renewal/upgrade CTA must render disabled (check [state] before
 * charging). A debug override lets the Developer Menu force the banner on/off for QA without touching
 * Remote Config; `null` override defers to the fetched value.
 */
class SunsetConfig {
    private val remote = MutableStateFlow(SunsetState())
    private val debugOverride = MutableStateFlow<Boolean?>(null)

    private val _state = MutableStateFlow(SunsetState())
    val state: StateFlow<SunsetState> = _state.asStateFlow()

    /** Push the fetched Remote Config values (empty messages are allowed — the UI supplies a fallback). */
    fun update(enabled: Boolean, message: LocalizedText) {
        remote.value = SunsetState(enabled = enabled, message = message)
        recompute()
    }

    /** Developer Menu override: true/false forces the banner; null returns to the Remote Config value. */
    fun setDebugOverride(enabled: Boolean?) {
        debugOverride.value = enabled
        recompute()
    }

    fun debugOverride(): Boolean? = debugOverride.value

    private fun recompute() {
        val effective = debugOverride.value ?: remote.value.enabled
        _state.value = remote.value.copy(enabled = effective)
    }
}
